/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import { ANALYTICS } from '../utils/enums';
import CatalogAnalytics from './catalogAnalytics';
import PDPAnalytics from './pdpAnalytics';

const ANALYTICS_ENUM = {
    CLICK: 'click',
    RENDER: 'render',
    BANNER: 'banner',
    VIDEO: 'video',
    CLOSE: 'close',
    BUTTON: 'button',
};

class BannerAnalytics {
    /*
     * Can be used to update singleton class object for new pdp data.
     * It takes the app object and sectionId (Category on side nav).
     */
    setBannerSource(source) {
        this.source = source;
    }

    setSectionId(sectionId, platform) {
        this.sectionId = sectionId;
        this.platform = platform;
    }

    setCurrentRoutingProps() {
        // All banner render events should be triggered from Apps,
        // and whether or not to send would be handled at framework level
        this.shouldSendAnalytics = true;
    }

    getSectionId() {
        return this.sectionId;
    }

    getPlatform() {
        return this.platform;
    }

    bannerRendered(
        appConfig, appsPanelSkeleton, analyticObj = {}, CTAText, isVideo, ingestId, renderGuid, name, ingestDescription = ''
    ) {
        switch (this.source) {
        case ANALYTICS.APPS:
            if (!this.shouldSendAnalytics) { return; }

            const eventObj = {
                type: ANALYTICS_ENUM.RENDER,
                subtype: ANALYTICS_ENUM.BANNER,
                CTAText,
                viewType: analyticObj.widthType,
                sectionId: this.sectionId,
                platform: this.platform,
                ingestId,
                renderGuid,
                foreground_only: 'true',
                name,
                ingestDescription,
            };
            if (isVideo) {
                eventObj.subtype = ANALYTICS_ENUM.VIDEO;
            }
            CatalogAnalytics.sendAnalytics(appConfig,
                appsPanelSkeleton, ANALYTICS.EVENTS.BANNER_EVENTS, eventObj);
            break;
        case ANALYTICS.PDP:
            const bannerConfig = {
                viewType: analyticObj.widthType,
                isVideo,
            };
            PDPAnalytics.bannerRenderEvent(bannerConfig, renderGuid);
            break;
        default:
        }
    }

    /*
     * send banner cta click event
     * analyticObj: {widthType, url, guid}
     * TODO: send guid & url in sendAnalytics
     */
    bannerCTAClicked(appConfig, appsPanelSkeleton, analyticObj, CTAText, ingestId, name, ingestDescription = '') {
        switch (this.source) {
        case ANALYTICS.APPS:
            const eventObj = {
                type: ANALYTICS_ENUM.CLICK,
                subtype: ANALYTICS_ENUM.BANNER,
                CTAText,
                viewType: analyticObj.widthType,
                sectionId: this.sectionId,
                platform: this.platform,
                guid: analyticObj.guid,
                url: analyticObj.url,
                ingestId,
                name,
                uiFilter: 'banner',
                ingestDescription,
                uiSearchKeyword: analyticObj.uiSearchKeyword,
            };
            CatalogAnalytics.sendAnalytics(appConfig,
                appsPanelSkeleton, ANALYTICS.EVENTS.BANNER_EVENTS, eventObj);
            break;
        case ANALYTICS.PDP:
            const bannerConfig = {
                viewType: analyticObj.widthType,
                CTAText,
                ingestDescription,
                guid: analyticObj.guid,
                url: analyticObj.url,
            };
            PDPAnalytics.bannerCTAClickEvent(bannerConfig);
            break;
        default:
        }
    }


    /**
     * Video click such that
     * it auto plays
     */
    videoPlayed(appConfig, appsPanelSkeleton, analyticObj) {
        switch (this.source) {
        case ANALYTICS.APPS:
            const eventObj = {
                type: ANALYTICS_ENUM.CLICK,
                subtype: ANALYTICS_ENUM.VIDEO,
                viewType: analyticObj.widthType,
                sectionId: this.sectionId,
                platform: this.platform,
            };
            CatalogAnalytics.sendAnalytics(appConfig,
                appsPanelSkeleton, ANALYTICS.EVENTS.BANNER_EVENTS, eventObj);
            break;
        case ANALYTICS.PDP:
            const bannerConfig = {
                viewType: analyticObj.widthType,
            };
            PDPAnalytics.playVideoEvent(bannerConfig);
            break;
        default:
        }
    }

    /**
     * Video close event
     */
    videoClosed(appConfig, appsPanelSkeleton, analyticObj) {
        switch (this.source) {
        case ANALYTICS.APPS:
            const eventObj = {
                type: ANALYTICS_ENUM.CLOSE,
                subtype: ANALYTICS_ENUM.VIDEO,
                viewType: analyticObj.widthType,
                sectionId: this.sectionId,
                platform: this.platform,
            };
            CatalogAnalytics.sendAnalytics(appConfig,
                appsPanelSkeleton, ANALYTICS.EVENTS.BANNER_EVENTS, eventObj);
            break;
        case ANALYTICS.PDP:
            const bannerConfig = {
                viewType: analyticObj.widthType,
            };
            PDPAnalytics.videoCloseEvent(bannerConfig);
            break;
        default:
        }
    }

    /**
     * @param { viewType } analyticObj
     * @param type: can be either
     * 'resume'/'pause'
     */
    videoControlEvent(appConfig, appsPanelSkeleton, analyticObj, type) {
        switch (this.source) {
        case ANALYTICS.APPS:
            const eventObj = {
                type,
                subtype: ANALYTICS_ENUM.VIDEO,
                viewType: analyticObj.widthType,
                sectionId: this.sectionId,
                platform: this.platform,
            };
            CatalogAnalytics.sendAnalytics(appConfig,
                appsPanelSkeleton, ANALYTICS.EVENTS.BANNER_EVENTS, eventObj);
            break;
        case ANALYTICS.PDP:
            const bannerConfig = {
                viewType: analyticObj.widthType,
                type,
            };
            PDPAnalytics.videoControlEvent(bannerConfig);
            break;
        default:
        }
    }

    productCtaRendered(appConfig, appsPanelSkeleton, analyticObj = {}) {
        const eventObj = {
            type: ANALYTICS_ENUM.RENDER,
            subtype: ANALYTICS_ENUM.BUTTON,
            sectionId: this.sectionId,
            platform: this.platform,
            context_guid: analyticObj.id,
            CTAText: analyticObj.CTAText,
            context_sequence: 'deep-link',
            name: analyticObj.name,
            ingestId: analyticObj.sapcode, // content.id
            version: analyticObj.version,
            guid: analyticObj.guid,
            renderGuid: analyticObj.renderGuid,
            foreground_only: 'true',
            svc_name: 'Direct',
        };
        CatalogAnalytics.sendAnalytics(appConfig,
            appsPanelSkeleton, ANALYTICS.EVENTS.BANNER_EVENTS, eventObj);
    }

    productCtaClicked(appConfig, appsPanelSkeleton, analyticObj = {}) {
        const eventObj = {
            type: ANALYTICS_ENUM.CLICK,
            subtype: ANALYTICS_ENUM.BUTTON,
            sectionId: this.sectionId,
            platform: this.platform,
            context_guid: analyticObj.id,
            CTAText: analyticObj.CTAText,
            context_sequence: 'deep-link',
            name: analyticObj.name,
            ingestId: analyticObj.sapcode, // content.id
            version: analyticObj.version,
            guid: analyticObj.guid,
            svc_name: 'Direct',
        };
        CatalogAnalytics.sendAnalytics(appConfig,
            appsPanelSkeleton, ANALYTICS.EVENTS.BANNER_EVENTS, eventObj);
    }

    bannerAnalyticsCallback(type, analyticObj, appConfig, appsPanelSkeleton) {
        switch (type) {
        case 'bannerRendered':
            this.bannerRendered(
                appConfig,
                appsPanelSkeleton,
                analyticObj.analytics,
                analyticObj.CTAText,
                analyticObj.isVideo,
                analyticObj.ingestId,
                analyticObj.renderGuid,
                analyticObj.name,
                analyticObj.ingestDescription
            );
            break;
        case 'bannerCTAClicked':
            this.bannerCTAClicked(
                appConfig,
                appsPanelSkeleton,
                analyticObj.analytics,
                analyticObj.CTAText,
                analyticObj.ingestId,
                analyticObj.name,
                analyticObj.ingestDescription,
            );
            break;
        case 'videoPlayed':
            this.videoPlayed(appConfig, appsPanelSkeleton, analyticObj.analytics);
            break;
        case 'videoClosed':
            this.videoClosed(appConfig, appsPanelSkeleton, analyticObj.analytics);
            break;
        case 'videoControlEvent':
            this.videoControlEvent(appConfig, appsPanelSkeleton,
                analyticObj.analytics, analyticObj.type);
            break;
        case 'productCtaRendered':
            this.productCtaRendered(appConfig, appsPanelSkeleton, analyticObj);
            break;
        case 'productCtaClicked':
            this.productCtaClicked(appConfig, appsPanelSkeleton, analyticObj);
            break;
        default:
            break;
        }
    }
}

export default new BannerAnalytics();
